import MarketingLayout from '../components/marketing/MarketingLayout';

export default function PrivacyPage() {
  return (
    <MarketingLayout>
      <section className="section-tight">
        <div className="container">
          <div className="doc">
        <h1 className="text-4xl font-bold text-navy-900 mb-4">Privacy Policy</h1>
        <p className="text-gray-600 mb-8">Effective date: February 18, 2026</p>
        <div className="prose prose-lg max-w-none">

          {/* 1. Introduction */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-4">
              Clarid, Inc. (&quot;Clarid,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) is committed to
              protecting the privacy and security of the information you provide when using our bank
              marketing compliance platform. This Privacy Policy describes how we collect, use, retain,
              and safeguard information when you access or use clarid.ai, our web application, our MCP
              (Model Context Protocol) server integration, our Microsoft Copilot Studio connector, and
              any other related services, tools, or APIs we operate (collectively, the &quot;Services&quot;).
            </p>
            <p className="text-gray-700 mb-4">
              By accessing or using the Services, you acknowledge that you have read and understood this
              Privacy Policy. If you are using the Services on behalf of a financial institution or other
              organization, you represent that you have the authority to bind that organization to this
              Privacy Policy. If you do not agree with our practices, please do not use the Services.
            </p>
            <p className="text-gray-700 mb-4">
              Clarid provides compliance-checking tools designed specifically for banks, credit unions,
              and other regulated financial institutions. We understand the sensitivity of operating in a
              regulated environment and have designed our data practices to reflect the heightened
              expectations of financial institution customers and their regulators.
            </p>
          </div>

          {/* 2. Information We Collect */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">2. Information We Collect</h2>
            <p className="text-gray-700 mb-4">
              We collect information that is necessary to provide, maintain, and improve the Services.
              The categories of information we collect are described below.
            </p>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">2.1 Account Information</h3>
            <p className="text-gray-700 mb-4">
              When you create an account or are invited to join an institution&apos;s workspace, we
              collect the following:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Full name</li>
              <li>Work email address</li>
              <li>Institution name and affiliation</li>
              <li>Role or job title within your institution</li>
              <li>Password (stored in hashed form only; we never store plaintext passwords)</li>
            </ul>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">2.2 Institution Information</h3>
            <p className="text-gray-700 mb-4">
              To configure compliance checks for your specific regulatory context, we collect:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Bank or credit union name</li>
              <li>FDIC certificate number or NCUA charter number</li>
              <li>Institution type (e.g., bank, credit union, savings association)</li>
              <li>State of charter or headquarters</li>
            </ul>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">2.3 Usage Data</h3>
            <p className="text-gray-700 mb-4">
              We automatically collect information about how you interact with the Services,
              including:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Compliance check submissions and their results</li>
              <li>Audit trail records (who checked what content and when)</li>
              <li>Feature usage patterns (which tools and features you use)</li>
              <li>Login timestamps and session duration</li>
              <li>Pages visited and actions taken within the application</li>
            </ul>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">2.4 Billing Information</h3>
            <p className="text-gray-700 mb-4">
              Payment processing is handled entirely by our third-party payment processor, Stripe, Inc.
              When you subscribe to a paid plan, Stripe collects and processes your payment card
              information directly. Clarid does <strong>not</strong> store, process, or have access to
              your full credit card numbers, debit card numbers, or bank account details. We receive
              only a limited set of billing metadata from Stripe, including the last four digits of your
              card, card brand, expiration date, billing address, and subscription status, solely for
              the purpose of displaying billing information in your account dashboard and generating
              invoices.
            </p>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">2.5 Technical Data</h3>
            <p className="text-gray-700 mb-4">
              When you access the Services, we automatically collect certain technical information,
              including:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Operating system and device information</li>
              <li>Session cookies and authentication tokens</li>
              <li>Referring URL and landing page</li>
              <li>Timezone and language preferences</li>
            </ul>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">2.6 Marketing Content Submitted for Compliance Checking</h3>
            <p className="text-gray-700 mb-4">
              The core function of Clarid is to analyze marketing content for regulatory compliance.
              When you submit content for review, we process the following types of materials:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Advertising copy and marketing text</li>
              <li>Email drafts and newsletter content</li>
              <li>Social media posts and captions</li>
              <li>Uploaded advertisement images and creative assets</li>
              <li>Website copy and landing page content</li>
              <li>Any other marketing materials you choose to submit for compliance review</li>
            </ul>
            <p className="text-gray-700 mb-4">
              This content is transmitted to our rules-based compliance engine for evaluation and is
              stored as part of your institution&apos;s audit trail. We do not use your submitted
              marketing content to train AI models, and we do not share it with any third party except
              as necessary to perform the compliance analysis (see Section 6).
            </p>
          </div>

          {/* 3. Information We Do NOT Collect */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">3. Information We Do NOT Collect</h2>
            <p className="text-gray-700 mb-4">
              Clarid is designed to analyze marketing content, not to access or process sensitive
              financial data. We want to be explicit about the categories of information we do
              <strong> not</strong> collect, access, or store. This distinction is critical for our
              banking and credit union customers and their regulatory obligations.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>Clarid does NOT collect, access, store, or process any of the following:</strong>
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>
                <strong>Consumer personally identifiable information (PII)</strong> — We do not collect
                or access names, Social Security numbers, dates of birth, physical addresses, phone
                numbers, or email addresses of your institution&apos;s customers or members.
              </li>
              <li>
                <strong>Financial account data</strong> — We do not collect or access bank account
                numbers, routing numbers, credit card numbers, debit card numbers, or any other
                financial account identifiers belonging to your customers.
              </li>
              <li>
                <strong>Core banking system data</strong> — We do not connect to, access, or ingest
                data from your core banking platform, loan origination systems, or any other internal
                banking systems. We do not access transaction records, account balances, or customer
                relationship data.
              </li>
              <li>
                <strong>Login credentials for other systems</strong> — We do not ask for or store
                passwords, API keys, or authentication credentials for any external system, including
                your core banking platform, email provider, or any other third-party service.
              </li>
              <li>
                <strong>Microsoft 365 tenant data</strong> — Our Microsoft Copilot Studio integration
                operates as an external tool. We do not access, read, or store your Microsoft 365
                tenant data, user directories, organizational structures, SharePoint content, Exchange
                mailboxes, Teams messages, or any other data within your Microsoft 365 environment
                beyond the specific draft marketing text that a user explicitly submits through the
                Copilot Studio connector.
              </li>
            </ul>
            <p className="text-gray-700 mb-4">
              If you believe that any consumer PII or sensitive financial data has been inadvertently
              submitted through the Services, please contact us immediately at{' '}
              <a href="mailto:support@clarid.ai" className="text-ocean-600 hover:text-ocean-700 underline">
                support@clarid.ai
              </a>{' '}
              so that we can promptly identify and delete the affected data.
            </p>
          </div>

          {/* 4. How We Use Information */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">4. How We Use Information</h2>
            <p className="text-gray-700 mb-4">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>
                <strong>Compliance analysis</strong> — To perform regulatory compliance checks on
                marketing content you submit, including evaluating content against applicable federal
                and state advertising regulations such as UDAP, UDAAP, Regulation Z, Regulation DD,
                Regulation E, HMDA, Fair Lending, CAN-SPAM, and FDIC/NCUA membership disclosure
                requirements.
              </li>
              <li>
                <strong>Audit trail generation</strong> — To create and maintain compliance audit
                records showing what content was reviewed, the analysis results, who submitted the
                content, and when the review occurred. These audit trails support your institution&apos;s
                regulatory examination preparedness.
              </li>
              <li>
                <strong>Report generation</strong> — To produce compliance reports, analytics
                dashboards, and exportable documentation for your internal compliance team, management,
                and regulators.
              </li>
              <li>
                <strong>Billing and subscription management</strong> — To process payments, manage your
                subscription plan, generate invoices, and communicate about billing-related matters.
              </li>
              <li>
                <strong>Transactional communications</strong> — To send essential service-related
                emails, including account verification, password resets, compliance check notifications,
                subscription confirmations, and important service updates.
              </li>
              <li>
                <strong>Service improvement</strong> — To analyze aggregated and anonymized usage
                patterns to improve the functionality, performance, and reliability of the Services.
              </li>
              <li>
                <strong>Security and fraud prevention</strong> — To detect, prevent, and respond to
                security incidents, unauthorized access, and fraudulent activity.
              </li>
              <li>
                <strong>Legal compliance</strong> — To comply with applicable laws, regulations,
                legal processes, or enforceable governmental requests.
              </li>
            </ul>
            <p className="text-gray-700 mb-4">
              <strong>We do NOT sell, rent, or share your personal information with third parties for
              their marketing purposes.</strong> We do not use your submitted marketing content, audit
              trail data, or compliance check results for advertising, profiling, or any purpose
              unrelated to providing the Services to you.
            </p>
          </div>

          {/* 5. Data Retention */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">5. Data Retention</h2>
            <p className="text-gray-700 mb-4">
              We retain different categories of data for different periods based on their purpose, your
              subscription plan, and applicable legal requirements.
            </p>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">5.1 Audit Trail and Compliance Records</h3>
            <p className="text-gray-700 mb-4">
              Compliance audit trail data, including submitted marketing content, analysis results,
              and review metadata, is retained for the duration specified by your subscription plan.
              Professional and Enterprise plan subscribers benefit from unlimited audit trail retention,
              ensuring that compliance records remain accessible for regulatory examinations regardless
              of when the original review was conducted. Starter plan subscribers receive audit trail
              retention as specified in their plan terms.
            </p>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">5.2 Account Data</h3>
            <p className="text-gray-700 mb-4">
              Your account information (name, email, role, institution affiliation) is retained for as
              long as your account remains active. If you or your institution administrator requests
              account deletion, or if your account is deactivated due to subscription cancellation, we
              will retain your account data for an additional 30 days to allow for account recovery or
              data export. After this 30-day grace period, your account data will be permanently deleted
              from our active systems. Residual copies in encrypted backups will be overwritten in
              accordance with our standard backup rotation schedule, typically within 90 days.
            </p>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">5.3 Billing Records</h3>
            <p className="text-gray-700 mb-4">
              Billing and payment records, including invoices, subscription history, and payment
              metadata received from Stripe, are retained for as long as required by applicable tax,
              accounting, and financial reporting laws. In most jurisdictions, this means billing records
              are retained for a minimum of seven (7) years from the date of the transaction.
            </p>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">5.4 Technical and Usage Logs</h3>
            <p className="text-gray-700 mb-4">
              Server logs, access logs, and technical usage data are retained for up to twelve (12)
              months for security monitoring and incident investigation purposes, after which they are
              automatically purged.
            </p>
          </div>

          {/* 6. Third-Party Services */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">6. Third-Party Services</h2>
            <p className="text-gray-700 mb-4">
              We use a limited number of carefully selected third-party service providers to operate
              the Services. Each provider has been evaluated for its security practices and data
              handling policies. We share only the minimum information necessary for each provider to
              perform its function.
            </p>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">6.1 Cloud Infrastructure</h3>
            <p className="text-gray-700 mb-4">
              Our application backend and database are hosted on Render, using data centers located
              within the United States. File storage and caching are provided by Microsoft Azure
              (Blob Storage and Cache for Redis), also in US data centers. Our frontend is hosted on
              Vercel. All data at rest is encrypted using provider-managed AES-256 encryption, and all
              data in transit is protected by TLS 1.2 or higher. Our cloud providers maintain SOC 2
              Type II certifications.
            </p>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">6.2 AI Analysis Providers</h3>
            <p className="text-gray-700 mb-4">
              We use a multi-provider AI architecture for compliance content analysis, with automatic
              failover between providers: OpenAI (GPT-4o, primary), Anthropic (Claude, secondary),
              and Google (Gemini, tertiary). When you submit content for a compliance check, only the
              marketing text or compliance data itself and relevant regulatory context are sent to the
              API — no personally identifiable information, account details, or institution-specific
              customer data is transmitted. None of these providers use data submitted through their
              APIs to train their models, as governed by their respective API terms of use. Responses
              are processed by Clarid and stored as part of your compliance audit trail.
            </p>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">6.3 Stripe</h3>
            <p className="text-gray-700 mb-4">
              Stripe, Inc. provides payment processing for all paid subscriptions. When you enter
              payment information, it is collected and processed directly by Stripe in accordance with
              Stripe&apos;s{' '}
              <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-ocean-600 hover:text-ocean-700 underline">
                Privacy Policy
              </a>.
              Stripe is a PCI DSS Level 1 certified service provider. Clarid does not receive, store,
              or process your full credit card or bank account numbers at any time.
            </p>

            <h3 className="text-xl font-semibold text-navy-800 mb-3">6.4 Resend</h3>
            <p className="text-gray-700 mb-4">
              Resend provides transactional email delivery for account-related communications, including
              account verification emails, password reset links, compliance check notifications, and
              subscription-related notices. Resend receives only the recipient email address and the
              email content necessary for delivery. We do not use Resend for marketing or promotional
              email campaigns.
            </p>
          </div>

          {/* 7. Data Security */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">7. Data Security</h2>
            <p className="text-gray-700 mb-4">
              We implement comprehensive technical and organizational security measures appropriate to
              the sensitivity of the data we handle and the expectations of our financial institution
              customers. Our security practices include:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>
                <strong>Encryption in transit</strong> — All data transmitted between your browser
                and our servers, and between our servers and third-party service providers, is
                encrypted using TLS 1.2 or higher. We enforce HTTPS on all endpoints and do not
                support unencrypted connections.
              </li>
              <li>
                <strong>Encryption at rest</strong> — All data stored in our databases and file
                storage systems is encrypted using AES-256 encryption. Database backups are also
                encrypted at rest.
              </li>
              <li>
                <strong>Multi-tenant data isolation</strong> — Each institution&apos;s data is
                logically isolated within our systems. Users from one institution cannot access
                compliance records, audit trails, or account data belonging to another institution.
                Tenant isolation is enforced at both the application layer and the database query
                layer.
              </li>
              <li>
                <strong>Role-based access control (RBAC)</strong> — Access to features and data
                within each institution&apos;s workspace is governed by role-based permissions. Roles
                include Admin, Compliance Officer, and Marketing User, each with defined access levels
                that limit visibility and actions to the minimum necessary for the role.
              </li>
              <li>
                <strong>API key security</strong> — API keys used for MCP server and programmatic
                access are stored in hashed form using industry-standard one-way hashing algorithms.
                We do not store plaintext API keys after initial generation.
              </li>
              <li>
                <strong>Session management</strong> — User sessions are managed using secure,
                HttpOnly, SameSite cookies with configurable expiration. Sessions are automatically
                invalidated after periods of inactivity.
              </li>
              <li>
                <strong>Infrastructure security</strong> — Our cloud infrastructure (Render for
                compute and database, Azure for file storage and caching, Vercel for frontend) is
                configured with provider-managed network security, automatic health checks, and
                continuous monitoring for unauthorized access attempts. All providers maintain
                SOC 2 Type II certifications.
              </li>
            </ul>
            <p className="text-gray-700 mb-4">
              While we implement robust security measures, no method of electronic transmission or
              storage is 100% secure. We continuously evaluate and improve our security practices to
              protect your data. If you become aware of any security vulnerability or unauthorized
              access, please notify us immediately at{' '}
              <a href="mailto:support@clarid.ai" className="text-ocean-600 hover:text-ocean-700 underline">
                support@clarid.ai
              </a>.
            </p>
          </div>

          {/* 8. Microsoft Copilot Integration */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">8. Microsoft Copilot Integration</h2>
            <p className="text-gray-700 mb-4">
              Clarid offers an integration with Microsoft Copilot through Copilot Studio that allows
              users to submit draft marketing content for compliance review directly from within the
              Microsoft 365 environment. This section describes how data flows through that integration
              and what information is and is not accessed.
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>
                <strong>Data transmitted</strong> — When a user invokes the Clarid compliance check
                via Copilot Studio, only the draft marketing text explicitly provided by the user is
                transmitted to Clarid for analysis. The compliance results are returned to the user
                within the Copilot interface.
              </li>
              <li>
                <strong>No Microsoft 365 tenant data accessed</strong> — Clarid does not access,
                read, index, or store any data from your Microsoft 365 tenant. This includes but is
                not limited to user directories, Active Directory or Entra ID information,
                organizational structures, SharePoint files, Exchange mailboxes, OneDrive contents,
                Teams conversations, or any other Microsoft 365 service data.
              </li>
              <li>
                <strong>External tool architecture</strong> — Clarid operates as an external tool
                called by Copilot Studio via a secure API connection. It does not operate within your
                Microsoft 365 tenant, does not require tenant-level admin consent beyond Copilot
                Studio configuration, and does not have delegated permissions to access Microsoft
                Graph API resources on behalf of your users.
              </li>
              <li>
                <strong>Authentication</strong> — The integration authenticates using a Clarid API
                key configured within your Copilot Studio environment. This key identifies your
                institution&apos;s Clarid workspace and enforces your plan&apos;s usage limits. No
                Microsoft 365 credentials are shared with Clarid.
              </li>
            </ul>
          </div>

          {/* 9. Your Rights */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">9. Your Rights</h2>
            <p className="text-gray-700 mb-4">
              We respect your rights over your personal information. Depending on your jurisdiction,
              you may have the following rights with respect to the personal information we hold about
              you:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>
                <strong>Right to access</strong> — You may request a copy of the personal information
                we hold about you, including account data, usage data, and audit trail records
                associated with your account.
              </li>
              <li>
                <strong>Right to deletion</strong> — You may request that we delete your personal
                information from our active systems. Please note that certain data may need to be
                retained for legal or regulatory compliance purposes as described in Section 5. If
                you are the sole administrator of an institution workspace, deletion of your account
                will also initiate deletion of the institution&apos;s workspace after the 30-day
                retention period.
              </li>
              <li>
                <strong>Right to data export</strong> — You may request an export of your data in a
                machine-readable format. This includes your account information, compliance check
                history, and audit trail records. Enterprise plan subscribers may also access data
                export functionality directly within the application.
              </li>
              <li>
                <strong>Right to update</strong> — You may update your account information, including
                your name, email address, and role, at any time through your account settings within
                the application. Institution administrators may also update institution-level
                information.
              </li>
              <li>
                <strong>Right to restrict processing</strong> — You may request that we restrict the
                processing of your personal information in certain circumstances, such as while we
                verify the accuracy of your data following a dispute.
              </li>
            </ul>
            <p className="text-gray-700 mb-4">
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:support@clarid.ai" className="text-ocean-600 hover:text-ocean-700 underline">
                support@clarid.ai
              </a>.
              We will respond to your request within thirty (30) days. We may ask you to verify your
              identity before processing your request to ensure the security of your account.
            </p>
          </div>

          {/* 10. Children's Privacy */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">10. Children&apos;s Privacy</h2>
            <p className="text-gray-700 mb-4">
              Clarid is a business-to-business (B2B) platform designed exclusively for use by
              compliance professionals, marketing teams, and other authorized personnel at financial
              institutions. The Services are not intended for, marketed to, or designed for use by
              individuals under the age of 13. We do not knowingly collect personal information from
              children under 13.
            </p>
            <p className="text-gray-700 mb-4">
              If we become aware that we have inadvertently collected personal information from a
              child under the age of 13, we will take prompt steps to delete such information from
              our systems. If you believe that a child under 13 has provided personal information to
              Clarid, please contact us immediately at{' '}
              <a href="mailto:support@clarid.ai" className="text-ocean-600 hover:text-ocean-700 underline">
                support@clarid.ai
              </a>.
            </p>
          </div>

          {/* 11. Changes to This Policy */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">11. Changes to This Policy</h2>
            <p className="text-gray-700 mb-4">
              We may update this Privacy Policy from time to time to reflect changes in our data
              practices, the Services, or applicable laws and regulations. When we make material
              changes to this Privacy Policy, we will notify you by sending an email to the address
              associated with your account at least fourteen (14) days before the changes take effect.
              We will also update the &quot;Effective date&quot; at the top of this page.
            </p>
            <p className="text-gray-700 mb-4">
              Non-material changes, such as clarifications or formatting updates that do not affect
              your rights, may be made without advance notice. We encourage you to review this Privacy
              Policy periodically to stay informed about how we protect your information.
            </p>
            <p className="text-gray-700 mb-4">
              Your continued use of the Services after the effective date of any updated Privacy Policy
              constitutes your acceptance of the revised terms. If you do not agree with any changes,
              you should discontinue use of the Services and contact us to request deletion of your
              account.
            </p>
          </div>

          {/* 12. Contact */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-navy-900 mb-4">12. Contact</h2>
            <p className="text-gray-700 mb-4">
              If you have any questions, concerns, or requests regarding this Privacy Policy, our data
              practices, or your personal information, please contact us using the information below:
            </p>
            <div className="bg-gray-50 rounded-lg p-6 mb-4">
              <p className="text-gray-700 mb-2"><strong>Clarid, Inc.</strong></p>
              <p className="text-gray-700 mb-2">
                Email:{' '}
                <a href="mailto:support@clarid.ai" className="text-ocean-600 hover:text-ocean-700 underline">
                  support@clarid.ai
                </a>
              </p>
            </div>
            <p className="text-gray-700 mb-4">
              We will make every effort to respond to your inquiry within a reasonable timeframe. For
              data access, deletion, or export requests, we will respond within thirty (30) days of
              receiving your verified request.
            </p>
          </div>

        </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
